import Spinner from "@/components/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useTopicById } from "@/hooks/topic-by.id.hook";
import { cn } from "@/lib/utils";
import { DimensionId, Topic } from "@/types/questionnaries";
import { ArrowDown2 } from "iconsax-react";
import { motion } from "motion/react";
import { FC, HTMLAttributes } from "react";

const badgeClassName = "rounded-full px-3 py-1 text-sm font-medium";

type TopicHeaderProps = HTMLAttributes<HTMLDivElement> & {
  topic: Topic;
  order: number;
  isOpen: boolean;
  showLoader: boolean;
  showWeight?: boolean;
  quizId: string;
  dimensionId: DimensionId;
};

const TopicHeader: FC<TopicHeaderProps> = ({
  topic,
  order,
  isOpen,
  showLoader,
  className,
  showWeight = true,
  quizId,
  dimensionId,
  ...props
}) => {
  const { data: userProfile } = useCurrentUserProfile();

  // topicDetails stores the same data as topic, but it refreshes constantly
  // when the answer changes. Bad design, but it works until question response
  // saving mechanism is updated.
  const { data: topicDetails } = useTopicById({
    companyId: userProfile?.companyId || "",
    applicationQuizId: quizId || "",
    dimension: dimensionId,
    topicId: topic.topic,
  });

  return (
    <div
      className={cn(
        "flex w-full cursor-pointer flex-row items-center gap-3 px-4 py-2 text-left hover:bg-secondary hover:no-underline",
        className,
      )}
      {...props}
    >
      <div className="grow">
        <h3 className="text-lg font-semibold text-[#293038]">
          {order}. {topic.name}
        </h3>

        {showWeight && topic.scoreProportion != null && (
          <div className="text-xs font-bold text-[#8090A3]">
            Puntaje Ponderado: {topic.scoreProportion} puntos
          </div>
        )}
      </div>

      {showLoader && <Spinner />}

      {topicDetails && (
        <div
          className={cn(
            "flex shrink-0 items-center gap-2",
            topicDetails.needsRefresh && "animate-pulse",
          )}
        >
          {topicDetails.respondedPercentage === 0 ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(badgeClassName, "bg-[#FCD4D7] text-[#F25E68]")}
                >
                  No iniciado
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Aún no has contestado ninguna pregunta
              </TooltipContent>
            </Tooltip>
          ) : topicDetails.completedPercentage === 100 ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(badgeClassName, "bg-[#9DE7B9] text-[#1c6c3c]")}
                >
                  Completado total
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">
                Se ha llenado toda la información requerida de las preguntas y
                las evidencias adicionales. Esto facilita la evaluación al tener
                mayor información.
              </TooltipContent>
            </Tooltip>
          ) : topicDetails.respondedPercentage === 100 &&
            topicDetails.respondedPercentage !==
              topicDetails.completedPercentage ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(badgeClassName, "bg-[#A7E0F6] text-[#0E688B]")}
                >
                  Completado básico
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Se ha llenado toda la información requerida mínima para las
                preguntas.
              </TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(badgeClassName, "bg-[#FFD082] text-[#B48221]")}
                >
                  En progreso
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Se ha llenado parte de la información de las preguntas pero aún
                no se ha completado.
              </TooltipContent>
            </Tooltip>
          )}

          <span
            className={cn(
              badgeClassName,
              "text-[#293038]",
              topicDetails.needsRefresh && "bg-gray-100",
            )}
            title={
              topicDetails.needsRefresh
                ? "Actualizando los datos..."
                : undefined
            }
          >
            {topicDetails.respondedQuestionsCount}/
            {topicDetails.totalQuestionsCount} preguntas
          </span>
        </div>
      )}

      <div className="flex items-center justify-center py-2.5">
        <motion.div
          initial={{ rotate: 0 }}
          animate={{ rotate: isOpen && !showLoader ? -180 : 0 }}
          transition={{ duration: 0.1 }}
        >
          <ArrowDown2 />
        </motion.div>
      </div>
    </div>
  );
};

export default TopicHeader;
